import React from 'react';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';

export interface ILifecycleStop {
  /** The stage's own name, e.g. `Active Customer`. */
  label: string;
  /** The line beneath it — a date, or the rule that would move the customer here. */
  caption?: string;
  reached: boolean;
}

export interface ILifecycleRailProps {
  stops: ILifecycleStop[];
  ariaLabel?: string;
  className?: string;
}

/* 3583-10341 draws the lifecycle as circular stops on one continuous track, not as the bordered
   boxes GateStepper draws - so this is its own component rather than a third GateStepper variant.
   GateStepper's node IS a padded bordered box, and its `flow` variant is the base branch's Growth
   Opportunities rail; branching its render would put that at risk for no gain here. PLA-8828. */
/* Drawn, off the scene graph of 3583-10341: every sdot is 37 x 37 at radius 100 with a 1.6px ring
   (3583-10344 filled, 3583-10352 empty) and every sline is 4px tall (3583-10343, 3583-10351). The
   ring is its own constant because at 4px the track is no longer the thickness the dot wants. */
const DOT_SIZE = 37;
const DOT_BORDER = 1.6;
const TRACK_HEIGHT = 4;

const Root = styled.ol`
  display: flex;
  margin: 0;
  padding: 0;
  list-style: none;
`;

/* Each stop owns an equal share of the width and its own half-segments of track, so the rail stays
   continuous at any container width - the frame's 1322px is a proportion, not a cap. */
const Stop = styled.li`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  flex-direction: column;
  align-items: center;
`;

const DotRow = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const Track = styled.span<{ $filled: boolean; $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => $side}: 0;
  width: 50%;
  height: ${TRACK_HEIGHT}px;
  transform: translateY(-50%);
  background: ${({ $filled }) => ($filled ? tokens.color.positive : tokens.color['border-subtle'])};
`;

const Dot = styled.span<{ $reached: boolean }>`
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  width: ${DOT_SIZE}px;
  height: ${DOT_SIZE}px;
  border: ${DOT_BORDER}px solid
    ${({ $reached }) => ($reached ? tokens.color.positive : tokens.color['border-subtle'])};
  border-radius: 50%;
  background: ${({ $reached }) =>
    $reached ? tokens.color.positive : tokens.color['surface-section']};
`;

/* DELIBERATE deviation, not parity: 3583-10341 draws the stop label at 10.5/12.8 and its caption at
   9.5/12, which is what shipped and is what the user could not read across a full-width panel. That
   frame under-scales its type throughout - it draws its own panel title at 13 where this screen
   renders card-title 18 - so the rail takes the roles of the panel it sits in instead: body for the
   stage name (the panel's own evidence values), card-description for the rule beneath it. Raised on
   the user's instruction, the same call as the Payment Insights view toggle. PLA-8828. */
const StopLabel = styled.span`
  margin-top: ${tokens.space['8']};
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['body-line']};
  text-align: center;
`;

const StopCaption = styled.span`
  margin-top: ${tokens.space['4']};
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['card-description-size']};
  line-height: ${tokens.type['card-description-line']};
  text-align: center;
`;

/**
 * The lifecycle stage rail — circular stops on a track, filled up to the stage the customer is in.
 */
const LifecycleRail: React.FC<ILifecycleRailProps> = ({ stops, ariaLabel, className }) => (
  <Root className={className} aria-label={ariaLabel} data-testid="lifecycle-rail">
    {stops.map((stop, index) => (
      <Stop key={stop.label} data-testid="lifecycle-rail-stop" aria-current={stop.reached}>
        <DotRow>
          {/* The track is drawn in halves so the first stop has nothing to its left and the last
              nothing to its right; a segment is filled only when BOTH stops it joins are reached. */}
          {index > 0 ? (
            <Track $side="left" $filled={stop.reached && stops[index - 1].reached} />
          ) : null}
          {index < stops.length - 1 ? (
            <Track $side="right" $filled={stop.reached && stops[index + 1].reached} />
          ) : null}
          <Dot
            $reached={stop.reached}
            data-testid="lifecycle-rail-dot"
            data-reached={stop.reached}
          />
        </DotRow>
        <StopLabel>{stop.label}</StopLabel>
        {stop.caption ? <StopCaption>{stop.caption}</StopCaption> : null}
      </Stop>
    ))}
  </Root>
);

export default LifecycleRail;
